import { useState } from "react";
import type { KeywordsSourceConfig, SourceConfig, TagFacet } from "@gooner/shared";
import { isOpenFacet, NEW_FACETS,
  FACET_ORDER,
  TAG_FACETS,
  facetLabel,
  facetOf,
  keywordGroups,
  tagHint,
  tagLabel,
} from "@gooner/shared";
import { cx } from "../ui/primitives";
import { Spinner } from "../ui/Spinner";
import { ChipGroup, TagChip } from "./TagChips";
import { IconPlus } from "../ui/icons";
import { type CollectionOption } from "../lib/monitorTemplates";
import { CrownMark } from "../ui/Crown";
import { VAULT_COLLECTION_ID, basePlaylistId, ownerScopedPlaylistId } from "@gooner/shared";

import { NewBadge } from "../../components/NewBadge";
import { useSession } from "../../lib/session";
import { trackVaultPlayed } from "../../lib/vault";
import { maskIf } from "../../lib/replayMask";

/*
 * The feed picker itself — the Collections chip row and the keyword facet groups — with
 * no opinion about WHICH region it edits. The editor's palette and the screen's region
 * popup both mount this; each host hands in the SHARED (leader-resolved) source and
 * routes the pick back to the leader itself, so the picker cannot clobber a follower's
 * kept-but-ignored snapshot by accident.
 *
 * A collection is a single-value source so it replaces the feed; keywords accumulate.
 */

/**
 * Turn a flat chip selection into the grouped source config.
 *
 * One group per facet, so two picks in the same facet OR together, and each facet ANDs
 * against the others — which is exactly what the grouped UI shows. Every non-facet
 * keyword shares a single "Other" group, matching the one heading they're listed under.
 */
export function keywordSourceOf(keywords: string[]): KeywordsSourceConfig {
  const byFacet = new Map<TagFacet, string[]>();
  const other: string[] = [];
  for (const k of keywords) {
    const facet = facetOf(k);
    if (!facet) {
      other.push(k);
      continue;
    }
    const group = byFacet.get(facet);
    if (group) group.push(k);
    else byFacet.set(facet, [k]);
  }
  const groups = [...byFacet.values()];
  if (other.length > 0) groups.push(other);
  return { kind: "keywords", groups };
}

export function FeedPicker({
  collections,
  tags,
  loading = false,
  source,
  onSetSource,
  onCreateCollection,
}: {
  collections: CollectionOption[];
  tags: string[];
  /**
   * Both chip lists are still being fetched. Without this the picker shows an empty
   * Collections row and no facet groups, which is exactly what it shows when the catalog
   * genuinely has nothing — so a slow fetch is indistinguishable from an empty library.
   */
  loading?: boolean;
  /** The config being edited — the host resolves a follower to its leader BEFORE
   *  handing this in, and lands onSetSource on that same leader. */
  source: SourceConfig;
  onSetSource: (s: SourceConfig) => void;
  /** Absent drops the "+ New" chip — the screen popup has no room for a detour
   *  into collection-building; the editor keeps it. */
  onCreateCollection?: () => void;
}) {
  // Flattened for the chip UI, which only asks "is this one picked". The grouping is
  // reconstructed on save by keywordSourceOf, so a region saved with groups round-trips
  // and one saved with the older flat list is read without a special case.
  const activeKeywords =
    source.kind === "keywords" ? keywordGroups(source).flat() : [];
  // Faps and Your Uploads are reserved ids: the region can only play them under the
  // owner-scoped `<id>:<uid>` form, which is also what the live source carries — so the
  // chip matches on the BASE id, or the one you just picked would never light up.
  const { user } = useSession();
  const activePlaylist = source.kind === "playlist" ? basePlaylistId(source.playlistId) : null;

  const toggleTag = (tag: string) => {
    const next = activeKeywords.includes(tag)
      ? activeKeywords.filter((t) => t !== tag)
      : [...activeKeywords, tag];
    onSetSource(next.length ? keywordSourceOf(next) : { kind: "random" });
  };

  // Same three tones as the clip-details pills: Editor's Choice in the accent
  // with a gold crown, your own private collections in violet, the rest neutral.
  const collectionChip = (c: CollectionOption, active: boolean) => {
    const tone = c.editorsChoice
      ? "bg-[var(--color-accent)]/20 text-[var(--color-accent-soft)]"
      : c.own
        ? "bg-violet-500/20 text-violet-300"
        : "bg-[var(--color-panel-2)] text-zinc-300";
    return cx(
      "rounded-full border px-2.5 py-1 text-xs transition-colors",
      tone,
      active
        ? "border-[var(--color-accent)] brightness-125"
        : "border-transparent hover:brightness-125",
    );
  };

  /* One spinner for both sections, since both come from the same pair of fetches and
     arrive together. The chip lists must stay INSIDE this branch: a spinner rendered
     above a visibly empty Collections row still shows the empty state it exists to hide. */
  return loading ? (
    <div className="flex flex-col items-center justify-center gap-2 py-10">
      <Spinner size={22} />
      <span className="text-xs text-zinc-500">
        Loading collections and tags…
      </span>
    </div>
  ) : (
    <>
      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          Collections
        </div>
        <div className="flex flex-wrap gap-1.5">
          {collections.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                const playlistId = ownerScopedPlaylistId(c.id, user?.id);
                if (!playlistId) return; // a reserved id with nobody to scope it to
                if (c.id === VAULT_COLLECTION_ID) trackVaultPlayed("picker");
                onSetSource({ kind: "playlist", playlistId });
              }}
              className={collectionChip(c, activePlaylist === c.id)}
              title={
                c.editorsChoice
                  ? "Editor's Choice"
                  : c.own
                    ? "Your collection"
                    : "Community"
              }
            >
              {/* Editor's Choice names are ours; everyone else's collection name is masked in replay. */}
              {c.editorsChoice ? <CrownMark size={11} /> : null} <span {...maskIf(!c.editorsChoice)}>{c.name}</span>
            </button>
          ))}
          {onCreateCollection ? (
            <button
              type="button"
              onClick={onCreateCollection}
              title="Create your own collection"
              aria-label="Create a collection"
              className="flex items-center gap-1 rounded-full border border-dashed border-[var(--color-edge)] px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-[var(--color-accent)]/60 hover:text-zinc-200"
            >
              <IconPlus width={12} height={12} />
              New
            </button>
          ) : null}
        </div>
      </div>

      <KeywordGroups
        tags={tags}
        selected={activeKeywords}
        onToggle={toggleTag}
      />
    </>
  );
}

/* Facet order and labels come from FACET_ORDER / facetLabel in shared — the same list the
   browse sidebar renders. This file used to keep its own shorter copy, which is how
   `clip_length` came to need adding in two places and how a facet could reach one surface
   without ever reaching the other. */

/**
 * Which keywords a region plays, by facet.
 *
 * The same collapsible chip groups the browse sidebar uses, and for the same reason: six
 * facets plus an open-ended keyword list is a wall of forty-odd bubbles, and the editor
 * has less room for it than Browse does — this panel also opens on top of a playing wall.
 *
 * Combining is the reason for the grouping: **OR inside a group, AND across groups**.
 * Picking Latina and Asian under Race plus Large under Breasts plays
 * `(Latina or Asian) and Large` — pick more within a group to widen, add a group to
 * narrow. A flat strip could only ever mean one of those.
 *
 * Every group starts OPEN here, unlike the browse sidebar where they start closed. The
 * two surfaces are answering different questions: Browse is filtering a catalog you
 * already know how to narrow, so a wall of values is noise until you ask for it, whereas
 * this panel IS the act of choosing what a region plays — collapsing it would hide the
 * only controls the panel exists for behind six headers.
 *
 * The groups still collapse, which is what makes the long tail of "Other" survivable.
 *
 * Multi-select, unlike Browse's one-value-per-facet: picks inside a group widen the
 * region and picks across groups narrow it, which is a different rule and stays here
 * rather than in the shared chip.
 *
 * Chips show display names — "Medium Tits", not `tits:medium`. The slug is what gets
 * saved and queried; it isn't something to read.
 */
/** Until when the keyword groups added 2026-09-12 (NEW_FACETS) wear "New!" here — a
 *  month after launch. Per surface, not shared: see NewBadge's header. */
const GROUPS_NEW_UNTIL = Date.UTC(2026, 9, 13);

function KeywordGroups({
  tags,
  selected,
  onToggle,
}: {
  /** Every catalog keyword available, as slugs. */
  tags: string[];
  selected: string[];
  onToggle: (tag: string) => void;
}) {
  // Only offer a facet value the catalog actually has a clip for — an empty chip is a
  // dead end here, unlike the browse sidebar where a zero still tells you something.
  const available = new Set(tags);
  // `tags` arrives most-used first (the catalog's tag list is count-sorted), so an open
  // facet's observed values keep that order after the declared seed values.
  const groups = FACET_ORDER.map((facet) => {
    const declared = TAG_FACETS[facet].map((v) => `${facet}:${v}`).filter((t) => available.has(t));
    const observed = isOpenFacet(facet) ? tags.filter((t) => facetOf(t) === facet && !declared.includes(t)) : [];
    return { key: facet as string, label: facetLabel(facet), values: [...declared, ...observed] };
  }).filter((g) => g.values.length > 0);

  const other = tags.filter((t) => facetOf(t) === null);
  const all = other.length
    ? [...groups, { key: "other", label: "Other", values: other }]
    : groups;

  // Seeded once from what the region already plays. Deliberately NOT kept in sync with
  // State, not a derived value: collapsing a group is a choice about this panel, and
  // re-deriving it every render would reopen a group the moment you touched a chip.
  const [open, setOpen] = useState<readonly string[]>(() =>
    all.map((g) => g.key),
  );
  const toggleOpen = (key: string) =>
    setOpen((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <div className="space-y-1">
      {all.map((g) => {
        const picked = g.values.filter((v) => selected.includes(v));
        return (
          <ChipGroup
            key={g.key}
            label={g.label}
            badge={
              (NEW_FACETS as readonly string[]).includes(g.key) && Date.now() < GROUPS_NEW_UNTIL
                ? <NewBadge variant="inline" />
                : null
            }
            // Every pick, not a count: "Latina, Asian" says what the region plays, where
            // "2 selected" makes you open the group to find out.
            summary={
              picked.length
                ? picked.map((t) => tagLabel(t)).join(", ")
                : null
            }
            open={open.includes(g.key)}
            onToggle={() => toggleOpen(g.key)}
          >
            {g.values.map((t) => (
              <TagChip
                key={t}
                label={tagLabel(t)}
                hint={tagHint(t)}
                active={selected.includes(t)}
                onClick={() => onToggle(t)}
              />
            ))}
          </ChipGroup>
        );
      })}

      <p className="pt-2 text-[11px] text-zinc-500">
        Picks in one group widen the region; picks in different groups narrow
        it. Latina and Asian plus Large plays clips that are (Latina or Asian)
        and Large.
      </p>
    </div>
  );
}
